import {
  keyboardState,
  keyboardGetters,
  keyboardMutations,
  keyboardActions,
} from "./proctoring/keyboardProctoring";
import {
  mouseState,
  mouseGetters,
  mouseMutations,
  mouseActions,
} from "./proctoring/mouseProctoring";
import {
  monitorState,
  monitorGetters,
  monitorMutations,
  monitorActions,
} from "./proctoring/monitorProctoring";
import {
  livekitState,
  livekitGetters,
  livekitMutations,
  livekitActions,
  _room,
} from "./proctoring/livekitProctoring";
import {
  cameraState,
  cameraGetters,
  cameraMutations,
  cameraActions,
  _cameraStream,
  _screenStream,
  captureImageFromStream,
} from "./proctoring/cameraProctoring";

import store from "@/store";
import { apiGet, apiPost, apiPatch, uploadToPresignedUrl } from "@/utils/api";
import { Track } from "livekit-client";

const state = () => ({
  ...keyboardState,
  ...mouseState,
  ...monitorState,
  ...livekitState,
  ...cameraState,

  isInitialized: false,
  initStatus: "idle",

  showViolationAlert: false,
  violationReason: "",
  lastViolationTime: 0,
  violationCooldown: 3000,
  violationsData: [],

  listenersActive: false,
  activeSection: "mcq",
  maxViolationsReached: false,
  adminWarningAcknowledged: false,
});

const getters = {
  ...keyboardGetters,
  ...mouseGetters,
  ...monitorGetters,
  ...livekitGetters,
  ...cameraGetters,

  isInitialized: (state) => state.isInitialized,
  initStatus: (state) => state.initStatus,
  isInitializing: (state) =>
    state.initStatus === "idle" || state.initStatus === "loading",
  showViolationAlert: (state) => state.showViolationAlert,
  violationReason: (state) => state.violationReason,
  violationsData: (state) => state.violationsData,
  maxViolationsReached: (state) => state.maxViolationsReached,
};

const mutations = {
  ...keyboardMutations,
  ...mouseMutations,
  ...monitorMutations,
  ...livekitMutations,
  ...cameraMutations,

  SET_MAX_VIOLATIONS_REACHED(state, val) {
    state.maxViolationsReached = val;
  },
  SET_INITIALIZED(state, val) {
    state.isInitialized = val;
  },
  SET_INIT_STATUS(state, val) {
    state.initStatus = val;
  },
  SET_VIOLATION_ALERT(state, { show, reason }) {
    state.showViolationAlert = show;
    state.violationReason = reason || "";
  },
  PUSH_VIOLATION(state, violation) {
    state.violationsData.push(violation);
  },
  SET_LAST_VIOLATION_TIME(state, time) {
    state.lastViolationTime = time;
  },
  SET_LISTENERS(state, val) {
    state.listenersActive = val;
  },
  SET_ACTIVE_SECTION(state, val) {
    state.activeSection = val;
  },
  RESET(state) {
    state.isInitialized = false;
    state.initStatus = "idle";
    state.showViolationAlert = false;
    state.violationReason = "";
    state.violationsData = [];
    state.lastViolationTime = 0;
    state.listenersActive = false;
    state.activeSection = "mcq";
    state.maxViolationsReached = false;
    state.adminWarningAcknowledged = false;

    // Livekit resets
    state.remoteParticipants = [];
    state.isExamStarted = false;
    state.chatMessages = [];
    state.showAdminWarning = false;
    state.kickedByProctor = false;
    state.screenShareTerminated = false;

    // Camera resets
    state.animationId = null;
    state.lastDetectionTime = 0;
    state.hasCamera = false;
    state.hasMic = false;
    state.hasScreen = false;
    state.isTfReady = false;
    state.eyesClosedStart = null;
    state.lookingAwayStart = null;
    state.multipleFaceStart = null;
    state.noFaceStart = null;
    state.objectDetectedStart = null;
    state.detectedObjects = [];
    state.isPaperPenModeActive = false;
  },
};

const actions = {
  ...keyboardActions,
  ...mouseActions,
  ...monitorActions,
  ...livekitActions,
  ...cameraActions,

  async initAll(
    { state, commit, dispatch },
    { roomName, participantName, role }
  ) {
    if (state.isInitialized && _cameraStream && _cameraStream.active) {
      return { success: true };
    }

    commit("SET_ROOM_INFO", { roomName, participantName, role });
    commit("SET_INIT_STATUS", "loading");

    try {
      await dispatch("initCamera");
      commit("SET_INIT_STATUS", "ready");
      commit("SET_INITIALIZED", true);

      dispatch("startProctoring");
      const isScribe =
        store.getters["examEligibility/isScribe"] ||
        localStorage.getItem("is_scribe") === "true";
      if (!isScribe) {
        setTimeout(() => dispatch("initTensorFlow"), 500);
      }
      return { success: true };
    } catch (err) {
      commit("SET_INIT_STATUS", "error");
      throw err;
    }
  },

  async _publishCamera() {
    if (!_room || !_room.localParticipant || !_cameraStream) return true;

    const videoTrack = _cameraStream.getVideoTracks()[0];
    const audioTrack = _cameraStream.getAudioTracks()[0];

    const existingVideo = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "camera");
    const existingAudio = Array.from(
      _room.localParticipant.audioTrackPublications.values()
    ).find((p) => p.trackName === "microphone");

    const republish = async (mediaTrack, existing, opts) => {
      if (!mediaTrack) return true;

      if (existing && existing.track) {
        if (existing.track.mediaStreamTrack === mediaTrack) {
          return true;
        }
        try {
          await _room.localParticipant.unpublishTrack(existing.track);
        } catch (err) {
          console.warn(
            `[Proctoring] Failed to unpublish stale ${opts.name} track`,
            err
          );
        }
      }

      try {
        const publication = await _room.localParticipant.publishTrack(
          mediaTrack,
          opts
        );
        return !!(publication && publication.track);
      } catch (err) {
        console.error(`[Proctoring] Failed to publish ${opts.name} track`, err);
        return false;
      }
    };

    const videoOk = await republish(videoTrack, existingVideo, {
      name: "camera",
      source: Track.Source.Camera,
      videoCodec: "h264",
      videoEncoding: { maxBitrate: 550000, maxFramerate: 10 },
    });
    await republish(audioTrack, existingAudio, {
      name: "microphone",
      source: Track.Source.Microphone,
    });

    return videoOk;
  },

  // Returns whether the screen track is confirmed published to the room
  // (i.e. LiveKit's SFU has accepted it, which is what makes it visible to
  // any admin auto-subscribed to this room). Returns true as a no-op when
  // the room isn't connected yet (e.g. the pre-exam consent step, before
  // connectRoom has run) -- that path republishes later inside connectRoom.
  async _publishScreen() {
    if (!_room || !_room.localParticipant || !_screenStream) return true;

    const screenTrack = _screenStream.getVideoTracks()[0];
    if (!screenTrack) return true;

    const existingScreen = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "screen");

    if (existingScreen && existingScreen.track) {
      if (existingScreen.track.mediaStreamTrack === screenTrack) {
        return true; // this exact track is already published
      }
      // A stale publication from a previous screen track can still be
      // registered for a moment after LiveKit's own auto-unpublish (fired
      // when that track ended) resolves -- clear it explicitly so a quick
      // re-share is never silently skipped by the "already published" check.
      try {
        await _room.localParticipant.unpublishTrack(existingScreen.track);
      } catch (err) {
        console.warn(
          "[Proctoring] Failed to unpublish stale screen track",
          err
        );
      }
    }

    try {
      const publication = await _room.localParticipant.publishTrack(
        screenTrack,
        {
          name: "screen",
          source: Track.Source.ScreenShare,
          videoCodec: "h264",
          videoEncoding: { maxBitrate: 600000, maxFramerate: 7 },
        }
      );
      return !!(publication && publication.track);
    } catch (err) {
      console.error("[Proctoring] Failed to publish screen track", err);
      return false;
    }
  },

  async triggerViolation({ state, commit, dispatch }, payload) {
    let reason = "";
    let isLog = false;
    let meta = null;

    if (typeof payload === "string") {
      reason = payload;
    } else {
      reason = payload.reason;
      isLog = payload.isLog || false;
      meta = payload.meta || null;
    }

    const isScribe =
      store.getters["examEligibility/isScribe"] ||
      localStorage.getItem("is_scribe") === "true";
    const isProctoringDisabled =
      store.getters["examEligibility/isProctoringDisabled"] ||
      localStorage.getItem("is_proctoring_disabled") === "true";

    if (
      isScribe &&
      (reason === "Speaking Detected" || reason === "Background noise detected")
    ) {
      return;
    }

    const exactMapping = {
      "Tab switching shortcut detected": false,
      "Screenshot attempt detected": false,
      "Developer tools access attempt": true,
      "View source attempt detected": true,
      "Page reload attempt detected": true,
      "Copy Attempt detected": false,
      "Paste Attempt detected": false,
      "Cut Attempt detected": false,
      "User switched browser tab": false,
      "User switched application / lost focus": true,
      "Right click attempt detected": true,
      "keydown Attempt detected": true,
      "visibilitychange Attempt detected": true,
      "blur Attempt detected": true,
      "contextmenu Attempt detected": true,
      "copy Attempt detected": false,
      "paste Attempt detected": false,
      "cut Attempt detected": false,
      "Looking away": false,
      "Prohibited object detected: cell phone": false,
      "Prohibited object detected: mobile phone": false,
      "Prohibited object detected: laptop": false,
      "Prohibited object detected: phone": false,
      "Prohibited object detected: tvmonitor": false,
      "Prohibited object detected: tv": false,
      "Prohibited object detected: book": true,
      "Prohibited object detected: monitor": false,
      "Speaking Detected": false,
      "Background noise detected": true,
      "Multiple displays detected": true,
    };

    if (exactMapping[reason] !== undefined) {
      isLog = exactMapping[reason];
    } else if (reason.startsWith("Prohibited object detected:")) {
      isLog = false;
    }

    if (state.activeSection === "descriptive") {
      isLog = true;
    }

    const isPriorityViolation =
      reason === "Person switch was detected" ||
      reason === "Multiple persons detected" ||
      reason === "Prohibited object detected: cell phone" ||
      reason === "Prohibited object detected: mobile phone" ||
      reason === "Prohibited object detected: phone";

    const now = Date.now();
    if (
      !isPriorityViolation &&
      now - state.lastViolationTime < state.violationCooldown
    )
      return;

    commit("SET_LAST_VIOLATION_TIME", now);

    let imageBlob = null;
    try {
      if (_cameraStream && _cameraStream.active) {
        imageBlob = await captureImageFromStream(_cameraStream);
      }
    } catch (err) {
      console.error("[Proctoring] Failed to capture event image", err);
    }

    const eventType = isLog ? "log" : "violation";

    if (!isLog) {
      commit("PUSH_VIOLATION", { reason, time: new Date().toISOString() });
    }

    const createdEvent = await dispatch("logEvent", {
      event_type: eventType,
      log_data: {
        reason,
        timestamp: new Date().toISOString(),
        event_type: eventType,
        ...(meta ? { meta } : {}),
      },
      imageBlob,
    });

    // The row we just created is the only thing the timeline gained, and the
    // create response already carries it. Re-pulling the entire paginated log
    // here meant every violation cost a second, much heavier request: a
    // COUNT, the student and exam header joins, and a presigned S3 URL per
    // row -- multiplied by every student sitting the exam.
    if (createdEvent) {
      window.dispatchEvent(
        new CustomEvent("attempt-event-logged", { detail: createdEvent })
      );
    }

    if (!isLog && !isScribe && !isProctoringDisabled) {
      const maxViolations = parseInt(
        localStorage.getItem("max_violations") || "0",
        10
      );
      if (maxViolations > 0 && state.violationsData.length >= maxViolations) {
        commit("SET_MAX_VIOLATIONS_REACHED", true);
      }
    }
  },

  startProctoring({ state, commit, dispatch }) {
    if (state.listenersActive) return;
    dispatch("startKeyboardProctoring");
    dispatch("startMouseProctoring");
    dispatch("startMonitorProctoring");
    commit("SET_LISTENERS", true);
  },

  stopProctoring({ commit, dispatch }) {
    dispatch("stopKeyboardProctoring");
    dispatch("stopMouseProctoring");
    dispatch("stopMonitorProctoring");
    commit("SET_LISTENERS", false);
  },

  dismissViolation({ commit }) {
    commit("SET_VIOLATION_ALERT", { show: false, reason: "" });
  },

  async cleanup({ commit, dispatch }) {
    dispatch("stopDetection");
    dispatch("stopProctoring");
    dispatch("stopNoiseDetection");
    dispatch("disconnectCameraStreams");
    await dispatch("disconnectLivekit");
    dispatch("cleanupExternalDevice");
    dispatch("cleanupPersonSwitch");
    dispatch("cleanupMultiplePerson");
    dispatch("cleanupFaceVisibility");
    dispatch("cleanupLookingAway");
    commit("RESET");
  },

  async terminateExam({ dispatch, commit }) {
    // createEvent("TERMINATED") is intentionally not called here
    // Termination is disabled — only cleanup if explicitly triggered (e.g. admin kick)
    await dispatch("cleanup");
    commit("SET_IS_EXAM_STARTED", false);
  },

  async createEvent({ dispatch }, status) {
    try {
      const student = store.state.user?.id;
      const exam = localStorage.getItem("exam_id");
      let attemptId = localStorage.getItem("exam_attempt_id");

      if (attemptId === "null" || attemptId === "undefined") {
        attemptId = null;
      }
      attemptId = attemptId || null;

      if (status === "IN_PROGRESS" && exam && !attemptId) {
        try {
          const res = await apiGet(
            `/exams/student-exams-available/?id=${exam}`
          );
          if (res?.data?.status === 200 && res.data.data) {
            const exams = Array.isArray(res.data.data)
              ? res.data.data
              : Object.values(res.data.data);
            const examData = exams.find((e) => e.id === exam);

            if (
              examData &&
              examData.attempts &&
              Array.isArray(examData.attempts)
            ) {
              const inProgress = examData.attempts.find(
                (a) => a.status === "IN_PROGRESS"
              );
              if (inProgress) {
                attemptId = inProgress.id;
                localStorage.setItem("exam_attempt_id", attemptId);
              }
            }
          }
        } catch (err) {
          console.error(
            "Failed to fetch exam attempts in proctoring store",
            err
          );
        }
      }

      const isEndingStatus = status === "COMPLETED" || status === "TERMINATED";

      if (attemptId) {
        await apiPatch(`/exam-delivery/exam-attempt-update/${attemptId}/`, {
          student,
          exam,
          status: status,
        });

        if (isEndingStatus) dispatch("clearExamSession");
        return;
      }

      const res = await apiPost("/exam-delivery/exam-attempt-create/", {
        student,
        exam,
        status: status,
      });

      const newAttemptId = res.data?.data?.id || res.data?.id;
      if (newAttemptId && status === "IN_PROGRESS") {
        localStorage.setItem("exam_attempt_id", newAttemptId);
      }

      if (isEndingStatus) dispatch("clearExamSession");
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
      throw err;
    }
  },

  clearExamSession() {
    localStorage.removeItem("exam_attempt_id");
    localStorage.removeItem("exam_attempt_boolean");
    localStorage.removeItem("exam_id");
    window.dispatchEvent(new CustomEvent("exam-completed"));
  },

  // Presigned upload lets the snapshot go straight to S3 instead of through
  // this multipart request -- a slow/degraded client connection then only
  // slows down its own upload, instead of tying up a backend request thread
  // for as long as it takes. Returns the image key and receipt, or null if the
  // presign or upload step failed (network hiccup, S3 issue, etc.) so the
  // caller can fall back to the multipart path rather than dropping the
  // image.
  async uploadEventImageViaPresign(_, imageBlob) {
    const contentType = imageBlob.type || "image/jpeg";
    try {
      const presign = await apiPost(
        "/exam-delivery/attempt-event-image-presign/",
        { content_type: contentType },
        { timeout: 3500 }
      );
      const { upload_url, upload_fields, image_key, image_token } =
        presign.data?.data || {};
      if (!upload_url || !upload_fields || !image_key || !image_token)
        return null;

      await uploadToPresignedUrl(
        upload_url,
        imageBlob,
        upload_fields,
        contentType
      );
      return { image_key, image_token };
    } catch (err) {
      console.error(
        "[Proctoring] Presigned image upload failed, falling back to direct upload",
        err
      );
      return null;
    }
  },

  async logEvent({ commit, dispatch }, { event_type, log_data, imageBlob }) {
    try {
      const student_id = store.state.user?.id;
      const exam_id = localStorage.getItem("exam_id");
      const attempt_id = localStorage.getItem("exam_attempt_id");

      let payload = { event_type, log_data, student_id, exam_id, attempt_id };

      if (imageBlob) {
        const uploadedImage = await dispatch(
          "uploadEventImageViaPresign",
          imageBlob
        );

        if (uploadedImage) {
          payload = { ...payload, ...uploadedImage };
        } else {
          // Presigned upload unavailable this time -- original multipart
          // path, kept so a presign/S3 hiccup never drops event logging.
          const formData = new FormData();
          formData.append("event_type", event_type);
          formData.append("log_data", JSON.stringify(log_data));
          if (student_id) formData.append("student_id", student_id);
          if (exam_id) formData.append("exam_id", exam_id);
          if (attempt_id) formData.append("attempt_id", attempt_id);
          formData.append("image", imageBlob, "violation.jpg");
          payload = formData;
        }
      }

      const res = await apiPost(
        "/exam-delivery/attempt-event-create/",
        payload
      );
      return res.data?.data || null;
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data?.data?.attempt_status === "MAX_ATTEMPT_REACHED"
      ) {
        commit("SET_MAX_VIOLATIONS_REACHED", true);
        dispatch("terminateExam");
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
